import { useMemo, useState, type MouseEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { message, Modal, Radio, Row, Space, Typography } from 'antd';
import { useLinksData } from '../../hooks/data/useLinksData';
import type { Link } from '../../types/domain';
import { useDataFilter } from '../../hooks/SimViewer/useDataFilter';

type Props = {
  wayIds: string[];
  isOpen: boolean;
  onClose: () => void;
};

type TripFilter = 'pass' | null;

const LinkFilterModal = ({ wayIds, isOpen, onClose }: Props): ReactNode => {
  const { dataFilter, updateLinkFilter } = useDataFilter();
  const { t } = useTranslation();
  const { links } = useLinksData();

  const selectedLinks = useMemo((): Link[] => {
    return wayIds
      .map((wayId: string): Link | undefined => {
        return links.get(wayId);
      })
      .filter((link: Link | undefined): link is Link => {
        return !!link;
      });
  }, [wayIds, links]);

  const defaultTripFilter = useMemo((): TripFilter => {
    if (selectedLinks.length === 0) {
      return null;
    }
    // すべてのリンクに通過地点フィルターが設定されているかどうかを確認する
    const isFiltered = selectedLinks.every((link: Link): boolean => {
      return dataFilter.pass.wayIds.includes(link.way_id);
    });
    return isFiltered ? 'pass' : null;
  }, [selectedLinks, dataFilter.pass.wayIds]);

  const [tripFilter, setTripFilter] = useState<TripFilter>(defaultTripFilter);

  const wayName = useMemo((): string => {
    const linkNames = selectedLinks.map((link: Link): string => {
      return link.way_name || link.way_id;
    });

    const uniqueNames = Object.entries(
      linkNames.reduce(
        (
          counts: Record<string, number>,
          name: string
        ): Record<string, number> => {
          counts[name] = (counts[name] || 0) + 1;
          return counts;
        },
        {}
      )
    ).map(([name, count]: [string, number]): string => {
      return `${name}${count > 1 ? `(${count})` : ''}`;
    });
    if (uniqueNames.length <= 5) {
      return uniqueNames.join(t('listDelimiter'));
    }
    return `${uniqueNames.slice(0, 5).join(t('listDelimiter'))} ... ${t('listOthers', { count: uniqueNames.length - 5 })}`;
  }, [selectedLinks, t]);

  const handleClickOk = (): void => {
    const condition = {
      start: false,
      end: false,
      pass: tripFilter === 'pass',
    };
    if (selectedLinks.length > 0) {
      updateLinkFilter(
        selectedLinks.map((link: Link): string => link.way_id),
        condition
      );
    }

    message.success(t('message.analysisConditionApplied', { name: wayName }));
    onClose();
  };

  const isSelectingMultipleLinks = selectedLinks.length > 1;
  const isFilterUnchanged = tripFilter === defaultTripFilter;

  return (
    <Modal
      title={wayName}
      centered
      okText={t('applyCondition')}
      cancelButtonProps={{ style: { display: 'none' } }}
      okButtonProps={{ size: 'small', disabled: isFilterUnchanged }}
      mask={false}
      keyboard={false}
      width={600}
      open={isOpen}
      onCancel={onClose}
      onOk={handleClickOk}
    >
      <Space direction="vertical" style={{ flex: 1 }}>
        <Typography.Text strong>{t('filterBy.move')}</Typography.Text>
        <Row gutter={[24, 12]}>
          <Space
            direction="vertical"
            style={{ width: '100%', marginTop: 8, marginLeft: 8 }}
          >
            <Radio
              checked={tripFilter === 'pass'}
              onClick={(event: MouseEvent): void => {
                event.preventDefault();
                setTripFilter((currentFilter: TripFilter): TripFilter => {
                  return currentFilter === 'pass' ? null : 'pass';
                });
              }}
            >
              {t('passed', {
                place: isSelectingMultipleLinks ? t('theseLinks') : wayName,
              })}
            </Radio>
          </Space>
        </Row>
      </Space>
    </Modal>
  );
};

export { LinkFilterModal };
